import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { ConfirmationRdv } from '@components/confirmation-rdv/models';
import { TranslatePipe } from '@ngx-translate/core';
import { AppointmentDetailsComponent } from '@shared/components/appointment-details';
import { ButtonComponent } from '@shared/components/button';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { LabelKey } from '@shared/models';
import { LogsService } from '@shared/services/logs';
import { RdvsService } from '@shared/services/rdvs';
import { CommonStore, FunnelStore } from '@stores';
import { EMPTY, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Component({
  selector: 'app-confirmation-rdv',
  imports: [SvgComponent, TitleComponent, AppointmentDetailsComponent, NgClass, ButtonComponent, TranslatePipe],
  templateUrl: './confirmation-rdv.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationRdvComponent extends AbstractDynamicComponent<ConfirmationRdv> implements OnInit, OnDestroy {
  readonly componentEl = viewChild<ElementRef<HTMLDivElement>>('component');
  readonly isSending = signal(false);
  readonly isSubmitted = signal(false);
  readonly answer = signal<LabelKey>(null!);
  private readonly router = inject(Router);
  private readonly funnelStore = inject(FunnelStore);
  readonly storeSelected = this.funnelStore.select('storeSelected');
  readonly slotSelected = this.funnelStore.select('slotSelected');
  readonly rdvId = this.funnelStore.select('rdvId');
  private readonly baseUrl = inject(CommonStore).select('baseUrl');
  private readonly rdvsService = inject(RdvsService);
  private readonly logs = inject(LogsService);

  ngOnInit(): void {
    const { storeSelected, slotSelected, userInfo, rdvId } = this.funnelStore.read();
    if (!storeSelected || !slotSelected || !userInfo || !rdvId) {
      this.onClose();
    }
  }

  ngOnDestroy(): void {
    // Reset the funnel whenever the confirmation screen is left, however the user leaves it
    // (close button, header navigation, browser back, starting a new appointment), so the
    // next RDV starts from a blank form instead of pre-filling the previous booking.
    this.funnelStore.set({ validatedStep: 0, currentStep: 1 });
  }

  onClose(): void {
    // The funnel reset is handled by ngOnDestroy when this screen is torn down.
    void this.router.navigate([this.baseUrl() ?? '']);
  }

  onAnswerSelected(option: LabelKey): void {
    this.answer.set(option);
    this.scrollToEnd();
  }

  onSubmit() {
    if (this.isSending()) return;
    this.isSending.set(true);
    this.rdvsService
      .updateProp(this.rdvId()!, { questionnaire: this.answer().label }, true)
      .pipe(
        take(1),
        tap(() => {
          this.logs.info('ConfirmationRdvComponent', 'The appointment has been successfully updated');
          this.isSubmitted.set(true);
        }),
        catchError((e) => {
          this.logs.error(
            'ConfirmationRdvComponent',
            'An error occurred while requesting the update of the appointment',
            e
          );
          this.isSending.set(false);
          return EMPTY;
        })
      )
      .subscribe();
  }

  private scrollToEnd(): void {
    const elRef = this.componentEl();
    if (!elRef) {
      return;
    }
    elRef.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }
}
